import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Section, Eyebrow } from '../ui/Section';
const faqs = [{
  q: 'What does an end-to-end managed IT partner manage?',
  a: 'CT Group manages the full technology environment your people use each day: infrastructure, software, licensing, users, support, security, hosting and voice. Rather than covering one piece, we coordinate the environment as one connected system, take responsibility for the outcome and reduce the number of suppliers your team has to chase.'
}, {
  q: 'Does CT Group specialise in law and accounting firms?',
  a: 'Yes. Professional services firms — particularly legal and accounting practices of roughly 30–100 seats — are our focus. That means our advice is shaped around practice management systems, document workflows, secure client data, reporting periods and the urgency fee-earning work demands, rather than generic small-business IT.'
}, {
  q: 'What is different about CT Group’s private infrastructure model?',
  a: 'CT Group builds and operates private cloud, server and data-centre infrastructure rather than simply reselling another provider’s platform. That gives clients more direct capability, clearer accountability and an environment that can be shaped around their practice applications, users, locations and risk — with one responsibility map instead of many handoffs.'
}, {
  q: 'What is included in the Independent IT Review?',
  a: 'The review examines your support model and responsibility map, security posture and Essential Eight considerations, hosting, backup and continuity dependencies, and software, licensing and vendor responsibilities. You receive prioritised recommendations in plain English, so decision-makers can see what is working and which issues deserve attention first.'
}, {
  q: 'Do we need to change providers to complete the review?',
  a: 'No. The Independent IT Review is a practical, non-intrusive exercise designed to give you clarity — not to force a switch. You retain full decision control. Many firms use the findings to understand their current environment better, whether or not they decide to change providers.'
}, {
  q: 'How does CT Group reduce the risk of switching providers?',
  a: 'We start with understanding, not disruption. There is no automatic rip-and-replace: dependencies are mapped before any action, changes are planned in stages, and scope and fees are confirmed before work begins. This considered approach keeps daily operations steady while responsibilities become clearer.'
}, {
  q: 'Can CT Group help us understand the Essential Eight?',
  a: 'Yes. We can explain the Essential Eight in plain language, the business risks it addresses and the questions a professional firm should be asking. Our Essential Eight Data Pack is a useful starting point, and the Independent IT Review considers where these controls fit within your wider environment.'
}, {
  q: 'How are managed IT services priced?',
  a: 'Managed IT pricing is shaped by factors such as user and device numbers, support coverage, security requirements, hosting and infrastructure, licensing responsibilities and any project work. We assess your environment, separate ongoing services from one-off projects and third-party licensing, then confirm scope, inclusions, responsibilities and fees before work begins.'
}, {
  q: 'Which locations does CT Group support?',
  a: 'CT Group was established in Perth and supports professional firms across Australia, including Melbourne, Sydney and Brisbane, with a growing east-coast presence. Support is delivered nationally, and hosting is provided from Australian infrastructure.'
}, {
  q: 'What should we prepare before the first conversation?',
  a: 'Nothing formal is required. It helps to have a general sense of your user count, key practice applications, current providers and any concerns or upcoming changes. The Independent IT Review is designed to uncover the detail, so an initial conversation can begin from wherever you are.'
}];
function FAQItem({
  q,
  a,
  open,
  onToggle,
  id
}: {
  q: string;
  a: string;
  open: boolean;
  onToggle: () => void;
  id: string;
}) {
  return <div className="border-b border-border" data-id="element-178">
      <h3 data-id="element-179">
        <button onClick={onToggle} aria-expanded={open} aria-controls={`${id}-panel`} id={`${id}-btn`} className="flex w-full items-center justify-between gap-4 py-5 text-left" data-id="element-180">
          <span className="font-display text-[17px] font-bold text-navy md:text-[18px]" data-id="element-181">
            {q}
          </span>
          <ChevronDown className={`h-5 w-5 flex-shrink-0 text-ct-deep transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden data-id="element-182" />
        </button>
      </h3>
      <div id={`${id}-panel`} role="region" aria-labelledby={`${id}-btn`} hidden={!open} className="pb-5 pr-8" data-id="element-183">
        <p className="text-[15px] leading-relaxed text-slate" data-id="element-184">{a}</p>
      </div>
    </div>;
}
export function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return <Section id="faq" className="bg-paper" ariaLabel="Frequently asked questions" data-id="element-185">
      <div className="mx-auto max-w-3xl" data-id="element-186">
        <div className="text-center" data-id="element-187">
          <Eyebrow data-id="element-188">Answers, in plain English</Eyebrow>
          <h2 className="mt-4 font-display text-[30px] font-black leading-tight text-navy md:text-[42px]" data-id="element-189">
            Frequently asked questions
          </h2>
        </div>
        <div className="mt-10" data-id="element-190">
          {faqs.map((f, i) => <FAQItem key={f.q} id={`faq-${i}`} q={f.q} a={f.a} open={open === i} onToggle={() => setOpen(open === i ? null : i)} data-id="element-191" />)}
        </div>
      </div>
    </Section>;
}